import React, { useEffect, useState } from "react";
import { FaCheck, FaSyncAlt, FaTimes } from "react-icons/fa";
import Layout from "../Layout/Layout";
import { isAuthenticated } from "../auth";
import {
    acceptContactRequest,
    declineContactRequest,
    listIncomingContactRequests,
    listMyContactRequests
} from "../auth/apiContactRequest";
import { elakayAutoText } from "../pageInside/dataElakay";
import "./contactRequestsInbox.css";

const text = (key, fallback) => elakayAutoText(`contact_requests_${key}`, fallback);

const formatDate = (value) => {
    if (!value) return "";
    return new Date(value).toLocaleString();
};

const StatusBadge = ({ status }) => (
    <span className={`contact-request-badge ${status}`}>
        {text(`status_${status}`, status)}
    </span>
);

const ContactRequestsInbox = ({ infoSource }) => {
    const auth = isAuthenticated() || {};
    const token = auth.token;

    const [incoming, setIncoming] = useState([]);
    const [mine, setMine] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const loadAll = async () => {
        if (!token) return;
        setLoading(true);
        setError("");

        const [incomingData, mineData] = await Promise.all([
            listIncomingContactRequests(token),
            listMyContactRequests(token)
        ]);

        if (incomingData?.success) setIncoming(incomingData.requests || []);
        if (mineData?.success) setMine(mineData.requests || []);

        if (!incomingData?.success || !mineData?.success) {
            setError(incomingData?.error || mineData?.error || text("loadError", elakayAutoText("h_4cf3ee7a7a20a3", "Unable to load contact requests.")));
        }

        setLoading(false);
    };

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleAccept = async (requestId) => {
        setNotice("");
        setError("");
        const data = await acceptContactRequest(token, requestId);
        if (data?.success) {
            setNotice(text("accepted", elakayAutoText("h_942cb171aec93b", "Request accepted. The message has been sent.")));
            await loadAll();
        } else {
            setError(data?.error || text("acceptError", elakayAutoText("h_ce2e8d295a05c9", "Unable to accept this request.")));
        }
    };

    const handleDecline = async (requestId) => {
        setNotice("");
        setError("");
        const data = await declineContactRequest(token, requestId);
        if (data?.success) {
            setNotice(text("declined", elakayAutoText("h_a7ba0552e34b71", "Request declined.")));
            await loadAll();
        } else {
            setError(data?.error || text("declineError", elakayAutoText("h_74409bc274be56", "Unable to decline this request.")));
        }
    };

    return (
        <Layout infoSource={infoSource} loading={false} SliderPunShow={false}>
            <main className="contact-requests-page" style={infoSource?.styleNav}>
                <div className="contact-requests-shell">
                    <header className="contact-requests-header">
                        <div>
                            <h1>{text("title", "Contact Requests")}</h1>
                            <p>{text("subtitle", "Requests from the Elakay assistant to connect with a responsible person.")}</p>
                        </div>
                        <button type="button" className="contact-requests-btn secondary" onClick={loadAll}>
                            <FaSyncAlt /> {text("refresh", "Refresh")}
                        </button>
                    </header>

                    {(notice || error) && (
                        <div className={`contact-requests-alert ${error ? "error" : "success"}`}>
                            {error || notice}
                        </div>
                    )}

                    <section className="contact-requests-section">
                        <h2>{text("incomingTitle", "Requests received")}</h2>
                        {loading && <p>{text("loading", "Loading...")}</p>}
                        {!loading && incoming.length === 0 && (
                            <p className="contact-requests-empty">{text("incomingEmpty", "No one has asked to contact you yet.")}</p>
                        )}
                        {!loading && incoming.length > 0 && (
                            <ul className="contact-requests-list">
                                {incoming.map((request) => (
                                    <li key={request._id} className="contact-requests-item">
                                        <div className="contact-requests-item-info">
                                            <strong>{request.customer?.firstname} {request.customer?.lastname}</strong>
                                            <span className="contact-requests-topic">{request.topicLabel || request.topic}</span>
                                            {request.question && <p className="contact-requests-question">"{request.question}"</p>}
                                            <small>{formatDate(request.createdAt)}</small>
                                        </div>
                                        <div className="contact-requests-item-actions">
                                            {request.status === "pending" ? (
                                                <>
                                                    <button type="button" className="contact-requests-btn primary" onClick={() => handleAccept(request._id)}>
                                                        <FaCheck /> {text("accept", "Accept")}
                                                    </button>
                                                    <button type="button" className="contact-requests-btn secondary" onClick={() => handleDecline(request._id)}>
                                                        <FaTimes /> {text("decline", "Decline")}
                                                    </button>
                                                </>
                                            ) : (
                                                <StatusBadge status={request.status} />
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    <section className="contact-requests-section">
                        <h2>{text("mineTitle", "Requests you sent")}</h2>
                        {!loading && mine.length === 0 && (
                            <p className="contact-requests-empty">{text("mineEmpty", "You haven't requested to contact anyone yet.")}</p>
                        )}
                        {!loading && mine.length > 0 && (
                            <ul className="contact-requests-list">
                                {mine.map((request) => (
                                    <li key={request._id} className="contact-requests-item">
                                        <div className="contact-requests-item-info">
                                            <strong>{request.topicLabel || request.topic}</strong>
                                            {request.question && <p className="contact-requests-question">"{request.question}"</p>}
                                            <small>{formatDate(request.createdAt)}</small>
                                        </div>
                                        <div className="contact-requests-item-actions">
                                            <StatusBadge status={request.status} />
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>
            </main>
        </Layout>
    );
};

export default ContactRequestsInbox;
